import { SLOTS, partById } from '../data/parts';
import { MAX_SLOT } from '../sim/loadout';
import { equippedId, ownedLevel } from '../sim/meta-parts';
import { applyMods } from '../sim/stats';
import { P, baseStats } from '../world/player';
/* ============================================================
   機庫 · 整機規格與「換上去會變怎樣」
   ============================================================

   **這是支柱五第 2 條的載體。** 支柱要求「部件之間是取捨不是階梯」，
   但玩家要感受得到取捨，前提是看得到取捨 —— 舊版的機庫只有一行文字說明
   （「結構上限 +80，但移速 −10%」），沒有任何地方能回答
   「所以我這台現在到底有多硬、換過去會少多少速度」。

   計算走 `sim/stats.ts` 的 `applyMods`，**與局內同一條路徑**。
   自己複製一份算法的話兩邊會漂移，而漂移的症狀是面板數字與實際不符，
   沒有任何測試會紅。 */

export type Spec = {
  hp: number; sh: number; dps: number; dmg: number; rate: number;
  speed: number; crit: number; dash: number; magnet: number;
  wSlot: number; pSlot: number; choices: number; revives: number;
  primaryMod: string | null;
};

/** 整機規格。`overSlot`/`overPart` 給定時，該部位改用指定部件試算（**不改 META**）。
    `overLv` 再指定假設等級，用來預覽「升一級會變怎樣」。

    沒買過的部件用 Lv1 試算 —— 那正是買下來當下會拿到的東西。 */
export function specOf(overSlot?: string, overPart?: string, overLv?: number): Spec {
  var mods: any[] = [];
  var g = { hp: 0, sh: 0, revives: 0, dash: 0, wSlot: 0, pSlot: 0, choices: 0, primaryMod: null as any };
  for (var i = 0; i < SLOTS.length; i++) {
    var slot = SLOTS[i].id;
    var id = slot === overSlot ? overPart : equippedId(slot);
    var p = partById(id);
    if (!p) continue;
    var lv = slot === overSlot && overLv ? overLv : Math.max(1, ownedLevel(id));
    if (p.mods) mods.push(p.mods(lv));
    var gg = p.grant ? p.grant(lv) : null;
    if (!gg) continue;
    g.hp += gg.hp || 0; g.sh += gg.sh || 0; g.revives += gg.revives || 0;
    g.dash += gg.dashCut || 0; g.wSlot += gg.wSlot || 0; g.pSlot += gg.pSlot || 0;
    g.choices += gg.choices || 0;
    if (gg.primaryMod) g.primaryMod = gg.primaryMod;
  }
  var mech = P.mech || { hp: 100, sh: 40, thrustMax: 120, speed: 11.5, stats: {} };
  var s = applyMods(P.base || baseStats(mech), mods);
  return {
    hp: mech.hp + g.hp, sh: mech.sh + g.sh,
    dmg: s.atkDmg, rate: s.atkRate, dps: s.atkDmg * s.atkRate,
    speed: s.speed, crit: s.crit, magnet: s.magnet,
    dash: mech.thrustMax,
    wSlot: MAX_SLOT + g.wSlot, pSlot: MAX_SLOT + g.pSlot,
    choices: 3 + g.choices, revives: 1 + g.revives,
    primaryMod: g.primaryMod
  };
}

/** 條列顯示的六項。挑的判準是「玩家做取捨時真正在權衡的東西」，
    不是「資料裡有的欄位」—— 射速與單發傷害各自看沒有意義，合成火力才有。 */
export var ROWS: Array<{ k: keyof Spec, n: string, fmt: (v: number) => string }> = [
  { k: 'hp', n: '結構', fmt: function (v) { return String(Math.round(v)); } },
  { k: 'sh', n: '護盾', fmt: function (v) { return String(Math.round(v)); } },
  { k: 'dps', n: '火力', fmt: function (v) { return v.toFixed(0); } },
  { k: 'speed', n: '機動', fmt: function (v) { return v.toFixed(1); } },
  { k: 'crit', n: '暴擊', fmt: function (v) { return Math.round(v * 100) + '%'; } },
  { k: 'dash', n: '衝刺', fmt: function (v) { return String(Math.round(v)); } }
];

var MOD_NAME = { pierce: '穿透', chain: '跳彈' };

/** 規則類的差異（槽位、選項數、復活、主武裝改裝）。
    這些不是數值而是規則，塞進上面那六格會讓「+1」的意思變得含糊。 */
export function ruleDiff(a: Spec, b: Spec) {
  var out: string[] = [];
  if (b.wSlot !== a.wSlot) out.push('武器槽 ' + a.wSlot + ' → ' + b.wSlot);
  if (b.pSlot !== a.pSlot) out.push('被動槽 ' + a.pSlot + ' → ' + b.pSlot);
  if (b.choices !== a.choices) out.push('升級選項 ' + a.choices + ' → ' + b.choices);
  if (b.revives !== a.revives) out.push('復活 ' + a.revives + ' → ' + b.revives);
  if (b.magnet !== a.magnet) out.push('吸取範圍 ' + a.magnet.toFixed(1) + ' → ' + b.magnet.toFixed(1));
  if (b.primaryMod !== a.primaryMod) {
    out.push('主武裝 ' + (MOD_NAME[a.primaryMod!] || '標準') + ' → ' + (MOD_NAME[b.primaryMod!] || '標準'));
  }
  return out;
}
